import PageMeta from "../../components/common/PageMeta";
import AuthLayout from "./AuthPageLayout";
import SignUpForm from "../../components/auth/SignUpForm";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SignUp: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation() as { state?: { notice?: string } };
  const [search] = useSearchParams();

  const notice: string | undefined = location?.state?.notice;
  const reason = search.get("reason");
  const fromSignIn = search.get("from") === "signin";

  const fallbackByReason: Record<string, string> = {
    "google-failed": t("auth.googleFailed", {
      defaultValue: "No pudimos iniciar sesión con Google. Regístrate para continuar.",
    }),
    "user-not-found": t("auth.userNotFound", {
      defaultValue: "No encontramos tu cuenta. Crea una nueva para continuar.",
    }),
  };

  const message = notice ?? (reason ? fallbackByReason[reason] : undefined);
  const isErrorMessage = fromSignIn || reason === "user-not-found";

  const SignUpRightPanel = (
    <div className="relative flex items-center justify-center z-10 w-full h-full p-12">
      <div className="max-w-lg">
        {/* Logo */}
        <div className="mb-8 text-center">
          <img
            src="/images/logo/logo-dark.svg"
            alt="Logrit.Life"
            className="h-12 mx-auto drop-shadow-lg mb-6"
          />
        </div>

        {/* Hero Section */}
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            ¡Únete a Logrit.Life!
          </h2>
          <p className="text-lg text-white/90 leading-relaxed">
            Comienza a organizar tu vida familiar de manera simple y efectiva.
          </p>
        </div>

        {/* Features Grid */}
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 rounded-[28px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-white/20" style={{ backgroundColor: '#aad08cff' }}>
            <div className="flex-shrink-0 w-10 h-10 bg-white/30 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-black mb-1">{t('auth.tareasyactividades')}</h3>
              <p className="text-white/80 text-sm font-semibold">
                Crea y asigna tareas a cada miembro de tu familia
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 rounded-[28px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-white/20" style={{ backgroundColor: '#fdbc52ff' }}>
            <div className="flex-shrink-0 w-10 h-10 bg-white/30 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-black mb-1">{t('auth.gestiondeintegrantes')}</h3>
              <p className="text-white/80 text-sm font-semibold">
                Agrega miembros y personaliza sus perfiles
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 rounded-[28px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-white/20" style={{ backgroundColor: '#ef6e6bff' }}>
            <div className="flex-shrink-0 w-10 h-10 bg-white/30 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-black mb-1">{t('auth.calendariocompartido')}</h3>
              <p className="text-white/80 text-sm font-semibold">
                Visualiza todos los eventos en un solo lugar
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-white/70 text-sm">
            Regístrate gratis y comienza en minutos
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PageMeta
        title={t("auth.reactjsSignupDashboardTailadminNextjsAdminDashboardTemplate")}
        description={t("auth.signUpPageDescription", {
          defaultValue: t('auth.creatucuentaparacomenzara'),
        })}
      />
      <AuthLayout right={SignUpRightPanel}>
        <SignUpForm
          banner={
            message ? (
              <div className={`p-4 rounded-lg border ${
                isErrorMessage 
                  ? 'border-red-300 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800' 
                  : 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'
              } flex items-start gap-3`}>
                {isErrorMessage && (
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <div className="flex-1">
                  <p className="font-medium">{message}</p>
                </div>
              </div>
            ) : undefined
          }
        />
      </AuthLayout>
    </>
  );
};

export default SignUp;
